import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChange,
    ViewChild
} from '@angular/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

import { IDataset } from '../../models/dataset';
import { IRange } from '../../models/range';
import { PlotsService } from '../../services/plots.service';

@Component({
    selector: 'sci-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: [ './image-viewer.component.scss' ]
})
export class ImageViewerComponent implements OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('timeline', { static: false }) timeline: ElementRef;
    @ViewChild('viewer', { static: false }) viewer: ElementRef;
    @Input() plotId: string;
    @Input() range: IRange;
    @Input() dataset: IDataset;

    data;
    crosshairX: number;
    height = 36;
    image = 'assets/images/sun_blank.png';
    imageViewerSubscribeToCrosshair: Subscription;
    margin: any = { top: 0, right: 8, bottom: 0, left: 8 };
    player;
    playing = false;
    selectedPoint: SVGPoint;
    ticksArray: number[];
    timelineLocation: number;
    timestamp: number;
    timestampRange: number[];
    trackLength: number;
    trackPath: string;
    transformString = `translate (${this.margin.left} ${this.margin.top})`;
    hoveredDate: number;
    timeLabelX: number;
    // viewBox width and height are used for calculating the bottom-padding for the inline styles and 'bottom-padding hack'
    // padding-bottom = viewBox.width / viewBox.height * 100%;
    // if margins or viewBox change, adjust bottom-padding on the svg element in html accordingly

    constructor(
        private _plotService: PlotsService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {}

    ngAfterViewInit() {
        this.trackLength = this.viewer.nativeElement.clientWidth;
        this.trackPath = `M 0 12 L ${this.trackLength} 12`;
        this.createTimeline();
        this.imageViewerSubscribeToCrosshair = this._plotService.xPosition$.subscribe( response => {
            if ( response && response.position && this.ticksArray ) {
                this.crosshairX =  this.getNearestTick( this.timestampToSvg( response.position ));
                this.hoveredDate = this.svgToTimestamp( this.crosshairX, this.timestampRange );
                this.setDateLabelPosition( this.crosshairX );
                this._changeDetectorRef.detectChanges();
            }
        });

        this._plotService.getPlotReflow$().subscribe( () => {
            this.trackLength = this.viewer.nativeElement.clientWidth - 16;
            this.trackPath = `M 8 12 L ${this.trackLength} 12`;
            this.createTimeline();
        });
        this.setDateLabelPosition( 0 );
        this._changeDetectorRef.detectChanges();
    }


    ngOnChanges( changes: {[propertyName: string]: SimpleChange} ) {
        this.rangeToTimestamps();
        if ( changes.dataset && this.dataset.data && this.dataset.data.length ) {
            this.data = this.dataset.data;
        }
        this.image = this.data[0][1];
    }

    createTimeline() {
        this.ticksArray = this.data.map( item => {
            return this.timestampToSvg( item[0] );
        });
        this.timestamp = this.data[0][0];
        this.selectedPoint = {
            x: this.timestampToSvg( this.timestamp ),
            y: 0
        } as any;
    }

    ngOnDestroy(): void {
        if (this.player) {
            clearTimeout(this.player);
        }
        if (this.imageViewerSubscribeToCrosshair) {
            this.imageViewerSubscribeToCrosshair.unsubscribe();
        }
    }

    getNearestTick( x: number ): number {
        return this.ticksArray.reduce( (prev, curr) => Math.abs( curr - x ) < Math.abs( prev - x ) ? curr : prev );
    }

    getNearestIndex( value: number ): number {
        return this.ticksArray.indexOf( value );
    }

    keyboardControls(event: KeyboardEvent): void {
        if ( this.ticksArray ) {
            switch ( event.key ) {
            case 'ArrowRight':
                this.step('forward');
                break;
            case 'ArrowLeft':
                this.step('backward');
                break;
            case ' ':
                this.playing ? this.pause() : this.play( this.selectedPoint );
                break;
            }
        }
    }

    onHover( event: MouseEvent ) {
        const mousePoint: SVGPoint = this.timeline.nativeElement.createSVGPoint();
        let svgPoint: SVGPoint = this.timeline.nativeElement.createSVGPoint();
        mousePoint.x = event.clientX;
        mousePoint.y = event.clientY;
        // tranform screen coordinates to svg coordinates
        svgPoint = mousePoint.matrixTransform( this.timeline.nativeElement.getScreenCTM().inverse() );
        svgPoint.y = 0;
        // remove margin transform
        svgPoint.x = svgPoint.x - this.margin.left;
        this._plotService.setXPosition( this.svgToTimestamp( svgPoint.x, this.timestampRange ), this.plotId );
    }

    pause(): void {
        this.selectedPoint.x = this.timelineLocation;
        this.playing = false;
        clearTimeout( this.player );
        const currentIndex = this.getNearestIndex( this.timelineLocation );
        this.timestamp = this.data[ currentIndex ][0];
        this.image = this.data[ currentIndex ][1];
    }

    play( point: SVGPoint ): void {
        const startingIndex = this.getNearestIndex( this.getNearestTick( point.x ) );
        const currentIndex = startingIndex === this.data.length - 1 ? 0 : startingIndex;
        // pre-load the images in the browser, calculate delay based on timestamp
        const loadedImages: HTMLImageElement[] = [];
        const delay: number[] = [];
        this.data.forEach( ( item, index ) => {
            loadedImages[ index ] = new Image( 320, 320 );
            loadedImages[ index ].src = this.data[ index ][ 1 ];
            const currentTick = this.ticksArray[ index ];
            const previousTick = this.ticksArray[ index - 1 ] || 0;
            delay[index] = +(( this.ticksArray.length / 10 ) * ( currentTick - previousTick )).toFixed();
        });
        this.timelineLocation = point.x;
        this.playing = true;
        const eachInterval = index => {
            this.timestamp = this.data[ index ][ 0 ];
            this.timelineLocation = this.timestampToSvg( this.timestamp );
            this.image = this.data[ index ][ 1 ];
            this.hoveredDate = this.timestamp;
            this.setDateLabelPosition( this.timelineLocation );
            // dispose of image after it is displayed
            loadedImages[ index ] = null;
            if ( index === this.data.length - 1 ) {
                this.playing = false;
                const lastIndex = this.data.length - 1;
                this.timestamp = this.data[ lastIndex ][ 0 ];
                this.selectedPoint.x = this.timestampToSvg( this.timestamp );
                this.image = this.data[ lastIndex ][ 1 ];
                return;
            }
            index++;
            this.player = setTimeout( eachInterval, delay[ index ], index);
        };
        this.player = setTimeout( eachInterval, delay[ currentIndex ], currentIndex );
    }

    rangeToTimestamps(): void {
        this.timestampRange = [ moment.utc( this.range.start ).valueOf(), moment.utc( this.range.end ).valueOf() ];
    }

    step(direction: string): void {
        const currentPoint: number = this.getNearestTick( this.selectedPoint.x );
        const currentIndex: number = this.getNearestIndex(currentPoint);
        let nextPoint: number;
        let nextIndex: number;
        if ( direction === 'forward' ) {
            nextPoint =
                this.ticksArray[ currentIndex + 1 ] ? this.ticksArray[ currentIndex + 1 ] : this.ticksArray[ this.ticksArray.length - 1 ];
            nextIndex =
                this.ticksArray[ currentIndex + 1 ] ? currentIndex + 1 : currentIndex;
        } else {
            nextPoint =
                this.ticksArray[ currentIndex - 1 ] ? this.ticksArray[ currentIndex - 1 ] : this.ticksArray[ 0 ];
            nextIndex =
                this.ticksArray[ currentIndex - 1 ] ? currentIndex - 1 : currentIndex;
        }
        this.selectedPoint.x = nextPoint;
        this.image = this.data[ nextIndex ][ 1 ];
        this.timestamp = this.data[ nextIndex ][ 0 ];
    }

    tapEvent( event: any ) {
        const mousePoint: SVGPoint = this.timeline.nativeElement.createSVGPoint();
        let svgPoint: SVGPoint = this.timeline.nativeElement.createSVGPoint();

        if (this.ticksArray) {
            this.playing = false;
            clearTimeout(this.player);
            // give the viewer focus
            this.viewer.nativeElement.focus();
            // get screen coordinates of click
            mousePoint.x = event.center.x;
            mousePoint.y = event.center.y;
            // tranform screen coordinates to svg coordinates
            svgPoint = mousePoint.matrixTransform(this.timeline.nativeElement.getScreenCTM().inverse());
            svgPoint.y = 0;
            // remove margin transform
            svgPoint.x = svgPoint.x - this.margin.left;
            // assign to nearest point in ticksArray
            const nearestTick = this.getNearestTick( svgPoint.x );
            const nearestIndex: number = this.getNearestIndex(nearestTick);
            this.selectedPoint.x = nearestTick;
            this.image = this.data[nearestIndex][1];
            this.timestamp = this.data[nearestIndex][0];
        }
    }

    svgToTimestamp( value: number, range: number[] ): number {
        return Math.round( (value * ( range[1] - range[0] ) / this.trackLength ) + range[0]);
    }

    timestampToSvg( value: number ): number {
        return +( ( value - this.timestampRange[0] ) / ( this.timestampRange[1] - this.timestampRange[0] ) * this.trackLength ).toFixed(2);
    }

    setDateLabelPosition( value: number ) {
        const timelabelWidth = 80; // found by inspection, might change if we change font
        if ( value + timelabelWidth > this.viewer.nativeElement.clientWidth ) {
            this.timeLabelX = this.viewer.nativeElement.clientWidth - timelabelWidth;
        } else if ( value - timelabelWidth < 0 ) {
            this.timeLabelX = timelabelWidth;
        } else {
            this.timeLabelX = value;
        }
    }
}
